import React, { useState, useMemo, useRef, useEffect } from 'react';
import Field from '../components/Field';
import { useAuth } from '../context/AuthContext';
import { usePrintSets } from '../hooks/usePrintSets';

const SimplifiedFolderRow = ({
    folder, level, activeFolderId, setActiveFolderId,
    expandedFolders, setExpandedFolders
}) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;

    const toggleFolder = (e) => {
        e.stopPropagation();
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folder.id)) next.delete(folder.id);
            else next.add(folder.id);
            return next;
        });
    };

    return (
        <div className="flex flex-col">
            <div
                className={`flex items-center gap-1 transition-all duration-200 py-0.5 px-2 rounded-md cursor-pointer ${activeFolderId === folder.id ? 'bg-blue-600/30 text-blue-400' : 'hover:bg-white/5 text-gray-400 hover:text-white'}`}
                style={{ paddingLeft: `${8 + (level * 12)}px` }}
                onClick={() => setActiveFolderId(folder.id)}
            >
                <button
                    className={`w-4 h-4 flex items-center justify-center text-[10px] transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} ${!hasChildren ? 'opacity-0' : 'text-gray-500 hover:text-white'}`}
                    onClick={toggleFolder}
                >
                    ▶
                </button>
                <div className="flex-1 text-[11px] truncate py-1">
                    {isExpanded && hasChildren ? '📂' : '📁'} {folder.name}
                </div>
            </div>
            {isExpanded && hasChildren && (
                <div className="flex flex-col">
                    {folder.children.map(child => (
                        <SimplifiedFolderRow
                            key={child.id}
                            folder={child}
                            level={level + 1}
                            activeFolderId={activeFolderId}
                            setActiveFolderId={setActiveFolderId}
                            expandedFolders={expandedFolders}
                            setExpandedFolders={setExpandedFolders}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function PrintModal({
    isOpen,
    onClose,
    allPlays = {},
    playbook = {},
    activePlay,
    playbookId,
    teamType,
    currentPlaybook,
    theme: appTheme = 'modern',
    globalSettings = {},
    wristbandGuide = { enabled: false, x: 50, y: 50, w: 40, h: 22.8 }
}) {
    const [mode, setMode] = useState('single'); // 'single' | 'wristband'
    const [printStyle, setPrintStyle] = useState('app'); // 'app' | 'ink-saver'

    // Wristband Cards State
    const [wristbandCards, setWristbandCards] = useState([{
        id: Date.now(),
        slots: Array(6).fill(null).map(() => ({ play: null, scale: 1.0, offsetX: 0, offsetY: 0 })),
        title: '',
        titleSize: 12,
        annotations: [],
        posX: 0.5, // inches
        posY: 0.5, // inches
        width: 4.25, // inches
        height: 2.33, // inches
        headerColor: '#000000'
    }]);

    const [printSettings, setPrintSettings] = useState({
        hideLabels: false,
        hideBall: false,
        hideFieldLines: false
    });

    // Single Play Pages State
    const [singlePages, setSinglePages] = useState([{
        id: Date.now() + 1,
        play: activePlay,
        scale: 1.2,
        offsetX: 0,
        offsetY: 0,
        title: activePlay?.playName || '',
        annotations: [],
        reads: [],
        hideRoutes: false
    }]);

    const flipPlay = (play) => {
        if (!play) return play;
        return {
            ...play,
            players: (play.players || []).map(p => ({ ...p, x: 100 - p.x })),
            routes: (play.routes || []).map(r => ({
                ...r,
                pointsAbs: (r.pointsAbs || []).map(pt => ({ ...pt, x: 100 - pt.x })),
                pointsRaw: (r.pointsRaw || []).map(pt => ({ ...pt, x: 100 - pt.x })),
                drawBase: r.drawBase ? { ...r.drawBase, x: 100 - r.drawBase.x } : r.drawBase
            })),
            ballSpec: play.ballSpec ? {
                ...play.ballSpec,
                startPos: play.ballSpec.startPos ? { ...play.ballSpec.startPos, x: 100 - play.ballSpec.startPos.x } : play.ballSpec.startPos
            } : play.ballSpec
        };
    };

    const [selectedSlot, setSelectedSlot] = useState(null); // { cardIdx, slotIdx } for wristband
    const [selectedPageIdx, setSelectedPageIdx] = useState(0); // for single play
    const [selectedAnnotationId, setSelectedAnnotationId] = useState(null); // { id, type: 'wristband'|'single' }
    const [globalScale, setGlobalScale] = useState(0.8);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [draggingCardIdx, setDraggingCardIdx] = useState(null);
    const [resizingCardIdx, setResizingCardIdx] = useState(null);
    const [slotContextMenu, setSlotContextMenu] = useState(null); // { x, y, cardIdx, slotIdx, mode }
    const [readsPicker, setReadsPicker] = useState(null); // { x, y, cardIdx, slotIdx, mode }
    const [showRulers, setShowRulers] = useState(true);
    const [activeFolderId, setActiveFolderId] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const dragStartLoc = useRef({ x: 0, y: 0 });
    const resizeStartSize = useRef({ width: 0, height: 0 });

    const { currentUser } = useAuth();
    const { printSets: cloudSets, savePrintSet: cloudSaveSet, deletePrintSet: cloudDeleteSet, loading: cloudLoading } = usePrintSets(playbookId);


    // Saved Print Sets Persistence
    const [localSets, setLocalSets] = useState([]);
    const isCloud = playbookId && playbookId !== 'local';

    useEffect(() => {
        const saved = localStorage.getItem('qr_saved_print_sets');
        if (saved) {
            try {
                setLocalSets(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to load saved print sets", e);
            }
        }
    }, []);

    const savedSets = isCloud ? cloudSets : localSets;

    const handleSaveSet = async (existingId = null) => {
        let name = "";
        if (existingId) {
            const existing = savedSets.find(s => s.id === existingId);
            name = existing.name;
        } else {
            name = prompt("Enter a name for this print set:");
        }

        if (!name) return;

        const prunePlay = (p) => {
            if (!p) return null;
            return {
                players: p.players || [],
                routes: p.routes || [],
                ballSpec: p.ballSpec || { enabled: false },
                playName: p.playName || '',
                formation: p.formation || '',
                teamType: p.teamType || 'Offense'
            };
        };

        const setData = {
            name,
            mode,
            wristbandCards: wristbandCards.map(card => ({
                ...card,
                slots: card.slots.map(slot => ({
                    ...slot,
                    play: prunePlay(slot.play)
                }))
            })),
            singlePages: singlePages.map(page => ({
                ...page,
                play: prunePlay(page.play)
            })),
            printSettings,
            printStyle,
            globalScale,
            timestamp: Date.now()
        };

        if (isCloud) {
            try {
                // Sanitize data before cloud save to remove undefineds or non-serializable parts
                const sanitizedData = JSON.parse(JSON.stringify(setData));
                await cloudSaveSet(existingId, sanitizedData, currentUser);
                // alert(`Set "${name}" synced to cloud!`);
            } catch (err) {
                console.error("Cloud save failed:", err);
                alert(`Failed to save to cloud: ${err.message || "Unknown error"}`);
            }
        } else {
            const newSet = {
                id: existingId || Date.now(),
                ...setData
            };

            let updated;
            if (existingId) {
                updated = localSets.map(s => s.id === existingId ? newSet : s);
            } else {
                updated = [...localSets, newSet];
            }

            localStorage.setItem('qr_saved_print_sets', JSON.stringify(updated));
            setLocalSets(updated);
            alert(`Set "${name}" saved locally!`);
        }
    };

    const handleLoadSet = (set) => {
        if (!confirm(`Load "${set.name}"? This will replace your current print layout.`)) return;
        setMode(set.mode || 'single');
        setWristbandCards(set.wristbandCards || []);
        setSinglePages(set.singlePages || []);
        setPrintSettings(set.printSettings || { hideLabels: false, hideBall: false, hideFieldLines: false });
        setPrintStyle(set.printStyle || 'app');
        setGlobalScale(set.globalScale || 0.8);
    };

    const handleDeleteSet = async (id) => {
        if (!confirm("Are you sure you want to delete this saved set?")) return;

        if (isCloud) {
            try {
                await cloudDeleteSet(id);
            } catch (err) {
                console.error("Cloud delete failed", err);
            }
        } else {
            const updated = localSets.filter(s => s.id !== id);
            localStorage.setItem('qr_saved_print_sets', JSON.stringify(updated));
            setLocalSets(updated);
        }
    };

    const activeFolderIds = useMemo(() => {
        if (!activeFolderId) return null;
        const allFolders = playbook?.folders || {};
        const descendants = [activeFolderId];
        const getChildren = (pid) => {
            Object.values(allFolders).forEach(f => {
                if (f.parentId === pid) {
                    descendants.push(f.id);
                    getChildren(f.id);
                }
            });
        };
        getChildren(activeFolderId);
        return new Set(descendants);
    }, [activeFolderId, playbook?.folders]);

    const folderTree = useMemo(() => {
        const list = Object.values(playbook?.folders || {});
        const map = {};
        const roots = [];
        list.forEach(f => {
            map[f.id] = { ...f, children: [] };
        });
        list.forEach(f => {
            if (f.parentId && map[f.parentId]) {
                map[f.parentId].children.push(map[f.id]);
            } else {
                roots.push(map[f.id]);
            }
        });

        const sortNodes = (nodes) => {
            nodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
            nodes.forEach(node => {
                if (node.children) sortNodes(node.children);
            });
        };
        sortNodes(roots);
        return roots;
    }, [playbook?.folders]);

    const filteredPlays = useMemo(() => {
        let list = Object.entries(allPlays).map(([id, p]) => ({ id, ...p }));

        // Folder Filter
        if (activeFolderIds) {
            list = list.filter(p => activeFolderIds.has(p.folderId));
        }

        if (!searchTerm) return list;
        return list.filter(p => (p.playName || p.id).toLowerCase().includes(searchTerm.toLowerCase()));
    }, [allPlays, searchTerm, activeFolderIds]);

    const handleAddWristbandCard = () => {
        // Determine reference card for size
        let refCard = null;
        if (selectedSlot && wristbandCards[selectedSlot.cardIdx]) {
            refCard = wristbandCards[selectedSlot.cardIdx];
        } else if (wristbandCards.length > 0) {
            refCard = wristbandCards[wristbandCards.length - 1];
        }

        setWristbandCards([...wristbandCards, {
            id: Date.now(),
            slots: Array(6).fill(null).map(() => ({ play: null, scale: 1.0, offsetX: 0, offsetY: 0 })),
            title: '',
            titleSize: 12,
            annotations: [],
            posX: 0.5 + (wristbandCards.length * 0.2),
            posY: 0.5 + (wristbandCards.length * 0.2),
            width: refCard ? refCard.width : 4.25,
            height: refCard ? refCard.height : 2.33,
            headerColor: '#000000'
        }]);
    };

    const handleCloneWristbandCard = (idx) => {
        const cardToClone = wristbandCards[idx];
        if (!cardToClone) return;

        // Perform a solid deep copy of the card data
        const clonedCard = JSON.parse(JSON.stringify(cardToClone));

        // Update unique properties
        clonedCard.id = Date.now();
        // Place it exactly below the original with a small 0.1 inch gap
        clonedCard.posY = cardToClone.posY + cardToClone.height + 0.1;
        // Shift right slightly so it's visible if it overlaps with something else, 
        // but user asked for "exactly below", so let's stick to X being the same.
        clonedCard.posX = cardToClone.posX;

        setWristbandCards([...wristbandCards, clonedCard]);
    };

    const handleAddSinglePage = () => {
        setSinglePages([...singlePages, {
            id: Date.now(),
            play: activePlay,
            scale: 1.2,
            offsetX: 0,
            offsetY: 0,
            title: '',
            titleSize: 24,
            annotations: [],
            hideRoutes: false
        }]);
    };

    const handleRemoveWristbandCard = (idx) => {
        if (wristbandCards.length === 1) return;
        setWristbandCards(wristbandCards.filter((_, i) => i !== idx));
        setSelectedSlot(null);
    };

    const handleRemoveSinglePage = (idx) => {
        if (singlePages.length === 1) return;
        setSinglePages(singlePages.filter((_, i) => i !== idx));
        if (selectedPageIdx >= singlePages.length - 1) setSelectedPageIdx(Math.max(0, singlePages.length - 2));
    };

    const handleAssignPlay = (cardIdx, slotIdx, play) => {
        if (mode === 'wristband') {
            const newCards = [...wristbandCards];

            const guideX = wristbandGuide.x || 50;
            const guideY = wristbandGuide.y || 50;

            // Simplified: Just center it relative to the guide center
            const targetScale = 2.5; // Fixed zoom factor for "Professional" look
            const targetOffsetX = (50 - guideX) * 3;
            const targetOffsetY = (50 - guideY) * 3;

            newCards[cardIdx].slots[slotIdx] = {
                ...newCards[cardIdx].slots[slotIdx],
                play,
                scale: targetScale,
                offsetX: targetOffsetX,
                offsetY: targetOffsetY,
                alias: '',
                subAlias: '',
                subAliasColor: '#ffffff'
            };

            // New: Default header color logic for dropped plays
            if (play && play.ballSpec && play.ballSpec.enabled) {
                const playPlayers = play.players || [];
                const segments = play.ballSpec.segments || [];
                let lastHolderId = play.ballSpec.currentHolderId;

                if (segments.length > 0) {
                    const lastSeg = segments[segments.length - 1];
                    // Final holder ID is usually toPlayerId (snap/handoff/throw) or playerId (post-catch carry)
                    lastHolderId = lastSeg.toPlayerId || lastSeg.catcherId || lastSeg.playerId || lastHolderId;
                }

                if (lastHolderId) {
                    const holder = playPlayers.find(p => p.id === lastHolderId);
                    if (holder && holder.color) {
                        // "If all players have the same color, ignore this rule."
                        const teamType = holder.type;
                        const sameTeam = playPlayers.filter(p => p.type === teamType);
                        const allSameColor = sameTeam.length > 0 && sameTeam.every(p => p.color === sameTeam[0].color);

                        if (!allSameColor) {
                            // "If there is a color already selected like in a save, use the existing color."
                            // We only apply this default if a slot header color isn't already assigned.
                            if (!newCards[cardIdx].slots[slotIdx].headerColor) {
                                newCards[cardIdx].slots[slotIdx].headerColor = holder.color;
                            }
                        }
                    }
                }
            }

            setWristbandCards(newCards);
            setSelectedSlot({ cardIdx, slotIdx });
        } else {
            const newPages = [...singlePages];
            newPages[cardIdx].play = play;
            setSinglePages(newPages);
            setSelectedPageIdx(cardIdx);
        }
    };

    const handleUpdateAlias = (cardIdx, slotIdx, newAlias, modeOverride = null) => {
        const activeMode = modeOverride || mode;
        if (activeMode === 'wristband') {
            const newCards = [...wristbandCards];
            newCards[cardIdx].slots[slotIdx].alias = newAlias;
            setWristbandCards(newCards);
        } else {
            const newPages = [...singlePages];
            newPages[cardIdx].title = newAlias;
            setSinglePages(newPages);
        }
    };

    const handleSwapSlots = (cardIdx, fromIdx, toIdx) => {
        if (fromIdx === toIdx) return;
        const newCards = [...wristbandCards];
        const slots = [...newCards[cardIdx].slots];
        const temp = slots[fromIdx];
        slots[fromIdx] = slots[toIdx];
        slots[toIdx] = temp;
        newCards[cardIdx].slots = slots;
        setWristbandCards(newCards);
        if (selectedSlot?.cardIdx === cardIdx) {
            if (selectedSlot.slotIdx === fromIdx) setSelectedSlot({ cardIdx, slotIdx: toIdx });
            else if (selectedSlot.slotIdx === toIdx) setSelectedSlot({ cardIdx, slotIdx: fromIdx });
        }
    };

    const handleUpdateSlot = (cardIdx, slotIdx, updates) => {
        const newCards = [...wristbandCards];
        newCards[cardIdx].slots[slotIdx] = { ...newCards[cardIdx].slots[slotIdx], ...updates };
        setWristbandCards(newCards);
    };

    const handleUpdateSinglePage = (pageIdx, updates) => {
        const newPages = [...singlePages];
        newPages[pageIdx] = { ...newPages[pageIdx], ...updates };
        setSinglePages(newPages);
    };

    const handleUpdateCard = (cardIdx, updates) => {
        const newCards = [...wristbandCards];
        newCards[cardIdx] = { ...newCards[cardIdx], ...updates };
        setWristbandCards(newCards);
    };

    const handleClearSlot = (cardIdx, slotIdx) => {
        const newCards = [...wristbandCards];
        newCards[cardIdx].slots[slotIdx] = { play: null, scale: 1.0, offsetX: 0, offsetY: 0 };
        setWristbandCards(newCards);
        if (selectedSlot?.cardIdx === cardIdx && selectedSlot?.slotIdx === slotIdx) {
            setSelectedSlot(null);
        }
    };

    const handleCardDragStart = (e, cardIdx) => {
        // Only start drag on double click logic handled separately or by specific trigger
        // Re-read user: "In order to move the card, I should have to double click"
        // We'll use a double click handler on the card instead.
    };

    const handleCardResizeStart = (e, cardIdx) => {
        e.stopPropagation();
        e.preventDefault();
        setResizingCardIdx(cardIdx);
        dragStartLoc.current = { x: e.clientX, y: e.clientY };
        resizeStartSize.current = {
            width: wristbandCards[cardIdx].width,
            height: wristbandCards[cardIdx].height
        };
    };

    const handleCardDragMove = (e) => {
        if (draggingCardIdx !== null) {
            const dx = (e.clientX - dragStartLoc.current.x) / (96 * globalScale);
            const dy = (e.clientY - dragStartLoc.current.y) / (96 * globalScale);
            const newCards = [...wristbandCards];
            newCards[draggingCardIdx].posX = dragStartLoc.current.startX + dx;
            newCards[draggingCardIdx].posY = dragStartLoc.current.startY + dy;
            setWristbandCards(newCards);
        } else if (resizingCardIdx !== null) {
            const dx = (e.clientX - dragStartLoc.current.x) / (96 * globalScale);
            const dy = (e.clientY - dragStartLoc.current.y) / (96 * globalScale);
            const newCards = [...wristbandCards];
            newCards[resizingCardIdx].width = Math.max(2.0, resizeStartSize.current.width + dx);
            newCards[resizingCardIdx].height = Math.max(1.0, resizeStartSize.current.height + dy);
            setWristbandCards(newCards);
        }
    };

    const handleCardDragEnd = () => {
        // We don't clear draggingCardIdx on MouseUp anymore because it's a toggle
        setResizingCardIdx(null);
    };

    const onCardDoubleClick = (e, cardIdx) => {
        e.stopPropagation();
        setDraggingCardIdx(cardIdx);
        dragStartLoc.current = {
            x: e.clientX,
            y: e.clientY,
            startX: wristbandCards[cardIdx].posX,
            startY: wristbandCards[cardIdx].posY
        };
    };

    const handleContainerClick = () => {
        if (draggingCardIdx !== null) {
            setDraggingCardIdx(null);
        }
        if (slotContextMenu !== null) {
            setSlotContextMenu(null);
        }
    };

    const handleAddAnnotation = (type, parentIdx) => {
        const newAnn = {
            id: Date.now(),
            text: 'EDIT ME',
            x: 50,
            y: 50,
            fontSize: type === 'wristband' ? 12 : 32,
            fontWeight: '900',
            color: '#000',
            type: 'text'
        };
        if (type === 'wristband') {
            const newCards = [...wristbandCards];
            newCards[parentIdx].annotations = [...(newCards[parentIdx].annotations || []), newAnn];
            setWristbandCards(newCards);
        } else {
            const newPages = [...singlePages];
            newPages[parentIdx].annotations = [...(newPages[parentIdx].annotations || []), newAnn];
            setSinglePages(newPages);
        }
    };

    const handleAddStamp = (color, parentIdx) => {
        let startX = 50;
        let startY = 50;

        // If a slot is selected on this card, position stamp in that slot's center
        if (selectedSlot && selectedSlot.cardIdx === parentIdx) {
            const col = selectedSlot.slotIdx % 3;
            const row = Math.floor(selectedSlot.slotIdx / 3);
            // 3 cols (33.33%), 2 rows (50%)
            // Center of slot: col*33.33 + 16.66
            startX = (col * 33.33) + 16.66;
            // Center of row: row*50 + 25
            startY = (row * 50) + 25;

            // Adjust slightly for title bar offset? 
            // The card height includes title bar. 
            // Title bar is 38px fixed. height is 2.33in (~223px). 38px/223px = ~17%.
            // Grid starts at 17%. Height of grid is 83%.
            // effective Row Height = 41.5%.
            // row 0 center = 17 + 20.75 = 37.75%
            // row 1 center = 17 + 41.5 + 20.75 = 79.25%
            startY = 17 + (row * 41.5) + 20.75;
        }

        const newStamp = {
            id: Date.now(),
            type: 'stamp',
            x: startX,
            y: startY,
            w: 10,
            h: 10,
            color: color
        };
        const newCards = [...wristbandCards];
        newCards[parentIdx].annotations = [...(newCards[parentIdx].annotations || []), newStamp];
        setWristbandCards(newCards);
    };

    const handleUpdateAnnotation = (annId, type, parentIdx, updates) => {
        if (type === 'wristband') {
            const newCards = [...wristbandCards];
            newCards[parentIdx].annotations = newCards[parentIdx].annotations.map(a => a.id === annId ? { ...a, ...updates } : a);
            setWristbandCards(newCards);
        } else {
            const newPages = [...singlePages];
            newPages[parentIdx].annotations = newPages[parentIdx].annotations.map(a => a.id === annId ? { ...a, ...updates } : a);
            setSinglePages(newPages);
        }
    };

    const handleDeleteAnnotation = (annId, type, parentIdx) => {
        if (type === 'wristband') {
            const newCards = [...wristbandCards];
            newCards[parentIdx].annotations = newCards[parentIdx].annotations.filter(a => a.id !== annId);
            setWristbandCards(newCards);
        } else {
            const newPages = [...singlePages];
            newPages[parentIdx].annotations = newPages[parentIdx].annotations.filter(a => a.id !== annId);
            setSinglePages(newPages);
        }
    };

    // INTERACTIVE PAN / ZOOM HANDLERS
    const onSlotWheel = (e, cardIdx, slotIdx) => {
        if (mode === 'wristband') {
            const slot = wristbandCards[cardIdx].slots[slotIdx];
            if (!slot.play) return;
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            const newScale = Math.max(0.5, Math.min(5.0, slot.scale + delta));
            handleUpdateSlot(cardIdx, slotIdx, { scale: newScale });
        } else {
            const page = singlePages[cardIdx];
            if (!page.play) return;
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            const newScale = Math.max(0.5, Math.min(5.0, page.scale + delta));
            handleUpdateSinglePage(cardIdx, { scale: newScale });
        }
    };

    const onSlotMouseDown = (e, cardIdx, slotIdx) => {
        if (mode === 'wristband') {
            const slot = wristbandCards[cardIdx].slots[slotIdx];
            // Always allow selection to access card settings
            setSelectedSlot({ cardIdx, slotIdx });

            if (slot.play) {
                setIsDragging(true);
                dragStartLoc.current = { x: e.clientX, y: e.clientY, startX: slot.offsetX, startY: slot.offsetY, type: 'wristband-slot', cardIdx, slotIdx };
            }
        } else {
            const page = singlePages[cardIdx];
            if (!page.play) return;
            setIsDragging(true);
            setSelectedPageIdx(cardIdx);
            dragStartLoc.current = { x: e.clientX, y: e.clientY, startX: page.offsetX, startY: page.offsetY, type: 'single-page', pageIdx: cardIdx };
        }
    };

    const onSlotMouseMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStartLoc.current.x;
        const dy = e.clientY - dragStartLoc.current.y;

        if (dragStartLoc.current.type === 'wristband-slot') {
            handleUpdateSlot(dragStartLoc.current.cardIdx, dragStartLoc.current.slotIdx, {
                offsetX: dragStartLoc.current.startX + dx / 2,
                offsetY: dragStartLoc.current.startY + dy / 2
            });
        } else if (dragStartLoc.current.type === 'single-page') {
            handleUpdateSinglePage(dragStartLoc.current.pageIdx, {
                offsetX: dragStartLoc.current.startX + dx / 1,
                offsetY: dragStartLoc.current.startY + dy / 1
            });
        } else if (dragStartLoc.current.type === 'annotation') {
            const { annId, parentType, parentIdx, startX, startY } = dragStartLoc.current;
            // Drag sensitivity - roughly pixels to %
            const factor = parentType === 'wristband' ? 0.25 : 0.1;
            handleUpdateAnnotation(annId, parentType, parentIdx, {
                x: startX + dx * factor,
                y: startY + dy * factor
            });
        }
    };

    const onSlotMouseUp = () => {
        setIsDragging(false);
    };

    const handlePrint = () => {
        window.print();
    };

    if (!isOpen) return null;

    const activeSlotData = selectedSlot ? wristbandCards[selectedSlot.cardIdx]?.slots[selectedSlot.slotIdx] : null;

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in"
            onMouseMove={onSlotMouseMove}
            onMouseUp={onSlotMouseUp}
            onMouseLeave={onSlotMouseUp}
        >
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <span className="text-2xl">🖨️</span> Print Manager
                        </h2>
                        <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
                            <button
                                className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${mode === 'single' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => { setMode('single'); setSelectedSlot(null); }}
                            >
                                Single Play
                            </button>
                            <button
                                className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${mode === 'wristband' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => setMode('wristband')}
                            >
                                Wrist Band Cards
                            </button>
                        </div>

                        <div className="h-6 w-[1px] bg-gray-700 mx-2" />

                        <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
                            <button
                                className={`px-4 py-1.5 rounded-md text-[10px] uppercase font-bold transition-all ${printStyle === 'app' ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => setPrintStyle('app')}
                                title="Use app colors and theme"
                            >
                                Professional
                            </button>
                            <button
                                className={`px-4 py-1.5 rounded-md text-[10px] uppercase font-bold transition-all ${printStyle === 'ink-saver' ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => setPrintStyle('ink-saver')}
                                title="Clean white background, high contrast"
                            >
                                Ink Saver
                            </button>
                        </div>


                    </div>
                    <div className="flex items-center gap-4">
                        {mode === 'wristband' ? (
                            <button
                                onClick={handleAddWristbandCard}
                                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg flex items-center gap-2"
                            >
                                <span className="text-lg">+</span> Add New Card
                            </button>
                        ) : (
                            <button
                                onClick={handleAddSinglePage}
                                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg flex items-center gap-2"
                            >
                                <span className="text-lg">+</span> Add New Page
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white text-2xl w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-800 transition-colors"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="flex-1 flex overflow-hidden">

                    {/* LEFT RAIL: Play Library & Selection Info */}
                    <div className="w-80 border-r border-gray-700 flex flex-col bg-gray-900/50">

                        {/* SAVED SETS SECTION */}
                        <div className="p-4 border-b border-gray-700 bg-gray-800/30">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    {isCloud ? 'Cloud Print Sets' : 'Local Print Sets'}
                                </h3>
                                <button
                                    onClick={() => handleSaveSet()}
                                    className="text-[10px] bg-green-600 hover:bg-green-500 text-white px-2 py-1 rounded font-bold transition-all active:scale-95 shadow-lg shadow-green-900/20"
                                >
                                    SAVE NEW
                                </button>
                            </div>

                            {savedSets.length === 0 ? (
                                <div className="text-[10px] text-gray-500 italic py-2 text-center border border-dashed border-gray-700 rounded">
                                    {isCloud && cloudLoading ? "Loading cloud sets..." : "No saved sets yet"}
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-1 qr-custom-scrollbar">
                                    {savedSets.map(set => (
                                        <div key={set.id} className="group flex items-center justify-between bg-gray-800/50 border border-gray-700 rounded-lg p-2 hover:border-blue-500 transition-all">
                                            <button
                                                onClick={() => handleLoadSet(set)}
                                                className="flex-1 text-left min-w-0"
                                            >
                                                <div className="text-xs font-bold text-white truncate">{set.name}</div>
                                                <div className="text-[8px] text-gray-500 uppercase flex items-center gap-1">
                                                    <span>{set.mode === 'wristband' ? '📿' : '📄'}</span>
                                                    <span>{set.mode === 'wristband' ? 'Wristband' : 'Single'}</span>
                                                    <span>•</span>
                                                    <span>{new Date(set.timestamp).toLocaleDateString()}</span>
                                                    {isCloud && set.updatedByName && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="text-blue-400 truncate max-w-[60px]">{set.updatedByName}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </button>
                                            <div className="flex gap-1 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleSaveSet(set.id)}
                                                    className="bg-gray-700 hover:bg-blue-600 text-[10px] w-6 h-6 flex items-center justify-center rounded transition-colors"
                                                    title="Overwrite this set with current layout"
                                                >
                                                    💾
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteSet(set.id)}
                                                    className="bg-gray-700 hover:bg-red-600 text-[10px] w-6 h-6 flex items-center justify-center rounded transition-colors"
                                                    title="Delete this set"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {mode === 'wristband' ? (
                            <>
                                {selectedSlot && (
                                    <div className="p-4 bg-blue-900/20 border-b border-blue-900/40 animate-in slide-in-from-top duration-200">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-blue-400 font-black text-[10px] uppercase tracking-widest">
                                                {activeSlotData?.play ? `Selected: ${activeSlotData.play.playName}` : `Card ${selectedSlot.cardIdx + 1} Settings`}
                                            </h3>
                                            <button onClick={() => setSelectedSlot(null)} className="text-blue-400 hover:text-white text-xs">Close</button>
                                        </div>

                                        {/* Card Header Color & Settings */}
                                        <div className="mb-4 space-y-3">
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Header Color</label>
                                                <div className="flex flex-wrap gap-1">
                                                    {['#000000', '#1f2937', '#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#6b7280', '#ffffff'].map(c => (
                                                        <button
                                                            key={c}
                                                            onClick={() => handleUpdateSlot(selectedSlot.cardIdx, selectedSlot.slotIdx, { headerColor: c })}
                                                            className={`w-5 h-5 rounded-full border border-gray-600 ${activeSlotData?.headerColor === c || (!activeSlotData?.headerColor && c === '#000000') ? 'ring-2 ring-white scale-110' : ''}`}
                                                            style={{ backgroundColor: c }}
                                                            title={c}
                                                        />
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Display Options</label>
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            id="hideLabels"
                                                            checked={printSettings.hideLabels}
                                                            onChange={(e) => setPrintSettings({ ...printSettings, hideLabels: e.target.checked })}
                                                            className="rounded bg-gray-800 border-gray-600 accent-blue-600"
                                                        />
                                                        <label htmlFor="hideLabels" className="text-xs text-white">Hide Labels</label>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            id="hideBall"
                                                            checked={printSettings.hideBall}
                                                            onChange={(e) => setPrintSettings({ ...printSettings, hideBall: e.target.checked })}
                                                            className="rounded bg-gray-800 border-gray-600 accent-blue-600"
                                                        />
                                                        <label htmlFor="hideBall" className="text-xs text-white">Hide Ball</label>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            id="hideFieldLines"
                                                            checked={printSettings.hideFieldLines}
                                                            onChange={(e) => setPrintSettings({ ...printSettings, hideFieldLines: e.target.checked })}
                                                            className="rounded bg-gray-800 border-gray-600 accent-blue-600"
                                                        />
                                                        <label htmlFor="hideFieldLines" className="text-xs text-white">Hide Field Lines</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Title Input */}
                                        <div className="mb-4">
                                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Card Title</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={wristbandCards[selectedSlot.cardIdx]?.title || ''}
                                                    onChange={(e) => handleUpdateCard(selectedSlot.cardIdx, { title: e.target.value })}
                                                    placeholder="e.g. Offensive Starters"
                                                    className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                                                />
                                                <div className="flex items-center gap-1 bg-gray-800 border border-gray-600 rounded px-1">
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { titleSize: (wristbandCards[selectedSlot.cardIdx].titleSize || 12) - 1 })}
                                                        className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-[10px] text-gray-400 w-4 text-center">{wristbandCards[selectedSlot.cardIdx]?.titleSize || 12}</span>
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { titleSize: (wristbandCards[selectedSlot.cardIdx].titleSize || 12) + 1 })}
                                                        className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <div className="flex justify-between items-center mb-1">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase">Card Title Height</label>
                                                <div className="flex items-center gap-1 bg-gray-800 border border-gray-600 rounded px-1">
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { titleHeight: (wristbandCards[selectedSlot.cardIdx].titleHeight || 38) - 2 })}
                                                        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-[10px] text-gray-400 w-4 text-center">{wristbandCards[selectedSlot.cardIdx]?.titleHeight || 38}</span>
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { titleHeight: (wristbandCards[selectedSlot.cardIdx].titleHeight || 38) + 2 })}
                                                        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex justify-between items-center mb-1">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase">Slot Header Height</label>
                                                <div className="flex items-center gap-1 bg-gray-800 border border-gray-600 rounded px-1">
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { headerHeight: (wristbandCards[selectedSlot.cardIdx].headerHeight || 12) - 1 })}
                                                        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-[10px] text-gray-400 w-4 text-center">{wristbandCards[selectedSlot.cardIdx]?.headerHeight || 12}</span>
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { headerHeight: (wristbandCards[selectedSlot.cardIdx].headerHeight || 12) + 1 })}
                                                        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex justify-between items-center mb-1">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase">Side Margin (L/R)</label>
                                                <div className="flex items-center gap-1 bg-gray-800 border border-gray-600 rounded px-1">
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { marginLR: Math.max(0, (wristbandCards[selectedSlot.cardIdx].marginLR || 0) - 0.05) })}
                                                        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-[10px] text-gray-400 w-8 text-center">{(wristbandCards[selectedSlot.cardIdx]?.marginLR || 0).toFixed(2)}"</span>
                                                    <button
                                                        onClick={() => handleUpdateCard(selectedSlot.cardIdx, { marginLR: (wristbandCards[selectedSlot.cardIdx].marginLR || 0) + 0.05 })}
                                                        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleAddAnnotation('wristband', selectedSlot.cardIdx)}
                                            className="w-full py-2 mb-2 bg-blue-600 hover:bg-blue-500 text-white rounded font-black text-[10px] uppercase shadow-lg"
                                        >
                                            + Add Floating Text
                                        </button>

                                        <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Add Stamp</label>
                                        <div className="grid grid-cols-5 gap-1 mb-3">
                                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#000000', '#6b7280', '#ffffff', '#db2777'].map(c => (
                                                <button
                                                    key={c}
                                                    onClick={() => handleAddStamp(c, selectedSlot.cardIdx)}
                                                    className="h-6 rounded border border-gray-600 hover:scale-110 transition-transform"
                                                    style={{ backgroundColor: c }}
                                                    title="Add colored stamp"
                                                />
                                            ))}
                                        </div>

                                        <div className="flex gap-2">
                                            {activeSlotData?.play && (
                                                <button
                                                    onClick={() => handleUpdateSlot(selectedSlot.cardIdx, selectedSlot.slotIdx, { offsetX: 0, offsetY: 0, scale: 1.0 })}
                                                    className="flex-1 py-2 text-[10px] font-bold text-blue-300 border border-blue-800 rounded hover:bg-blue-800 transition-colors"
                                                >
                                                    Reset Positioning
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleCloneWristbandCard(selectedSlot.cardIdx)}
                                                className="flex-1 py-2 text-[10px] font-bold text-green-400 border border-green-900 rounded hover:bg-green-900 transition-colors"
                                            >
                                                Clone Card
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="p-4 border-b border-gray-700 bg-gray-950/20">
                                    <h3 className="text-[10px] font-bold text-gray-500 uppercase mb-3">Play Library</h3>

                                    {/* Folder Navigation */}
                                    <div className="mb-4 bg-gray-900/50 rounded-lg border border-gray-700 p-2 max-h-48 overflow-y-auto qr-custom-scrollbar">
                                        <div
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${!activeFolderId ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-white/5'}`}
                                            onClick={() => setActiveFolderId(null)}
                                        >
                                            <span>📂</span> All Plays
                                        </div>
                                        {folderTree.map(f => (
                                            <SimplifiedFolderRow
                                                key={f.id}
                                                folder={f}
                                                level={0}
                                                activeFolderId={activeFolderId}
                                                setActiveFolderId={setActiveFolderId}
                                                expandedFolders={expandedFolders}
                                                setExpandedFolders={setExpandedFolders}
                                            />
                                        ))}
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="Search plays..."
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                    {filteredPlays.map(p => (
                                        <div
                                            key={p.id}
                                            draggable
                                            onDragStart={(e) => e.dataTransfer.setData('play', JSON.stringify(p))}
                                            className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg cursor-grab hover:bg-gray-700 transition-colors group"
                                        >
                                            <div className="text-sm font-bold text-white truncate">{p.playName || 'Untitled'}</div>
                                            <div className="text-[10px] text-gray-500 uppercase tracking-wider">{p.formation || 'MISC'}</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col flex-1 overflow-hidden">
                                <div className="p-4 bg-blue-900/20 border-b border-blue-900/40">
                                    <h3 className="text-blue-400 font-black text-[10px] uppercase tracking-widest mb-4">Page {selectedPageIdx + 1} Settings</h3>

                                    <div className="mb-4">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Page Title</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={singlePages[selectedPageIdx]?.title || ''}
                                                onChange={(e) => handleUpdateSinglePage(selectedPageIdx, { title: e.target.value })}
                                                className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                                            />
                                            <div className="flex items-center gap-1 bg-gray-800 border border-gray-600 rounded px-1">
                                                <button onClick={() => handleUpdateSinglePage(selectedPageIdx, { titleSize: (singlePages[selectedPageIdx].titleSize || 48) - 2 })} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white rounded">-</button>
                                                <button onClick={() => handleUpdateSinglePage(selectedPageIdx, { titleSize: (singlePages[selectedPageIdx].titleSize || 48) + 2 })} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white rounded">+</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Display Options</label>
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="singleHideLabels"
                                                    checked={printSettings.hideLabels}
                                                    onChange={(e) => setPrintSettings({ ...printSettings, hideLabels: e.target.checked })}
                                                    className="rounded bg-gray-800 border-gray-600 accent-blue-600"
                                                />
                                                <label htmlFor="singleHideLabels" className="text-xs text-white">Hide Labels</label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="singleHideBall"
                                                    checked={printSettings.hideBall}
                                                    onChange={(e) => setPrintSettings({ ...printSettings, hideBall: e.target.checked })}
                                                    className="rounded bg-gray-800 border-gray-600 accent-blue-600"
                                                />
                                                <label htmlFor="singleHideBall" className="text-xs text-white">Hide Ball</label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="singleHideFieldLines"
                                                    checked={printSettings.hideFieldLines}
                                                    onChange={(e) => setPrintSettings({ ...printSettings, hideFieldLines: e.target.checked })}
                                                    className="rounded bg-gray-800 border-gray-600 accent-blue-600"
                                                />
                                                <label htmlFor="singleHideFieldLines" className="text-xs text-white">Hide Field Lines</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2 mb-4">
                                        <button
                                            onClick={() => {
                                                const page = singlePages[selectedPageIdx];
                                                if (!page.play) return;
                                                handleUpdateSinglePage(selectedPageIdx, { play: flipPlay(page.play) });
                                            }}
                                            className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded font-bold text-[10px] uppercase border border-gray-600 transition-all flex items-center justify-center gap-2"
                                        >
                                            <span>↔️</span> Flip Play
                                        </button>
                                        <button
                                            onClick={() => {
                                                const page = singlePages[selectedPageIdx];
                                                handleUpdateSinglePage(selectedPageIdx, { hideRoutes: !page.hideRoutes });
                                            }}
                                            className={`flex-1 py-2 rounded font-bold text-[10px] uppercase border transition-all flex items-center justify-center gap-2 ${singlePages[selectedPageIdx]?.hideRoutes ? 'bg-orange-600/30 border-orange-500 text-orange-400' : 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'}`}
                                        >
                                            <span>{singlePages[selectedPageIdx]?.hideRoutes ? '👁️' : '🚫'}</span> {singlePages[selectedPageIdx]?.hideRoutes ? 'Show Routes' : 'Hide Routes'}
                                        </button>
                                    </div>

                                    <button
                                        onClick={() => handleAddAnnotation('single', selectedPageIdx)}
                                        className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white rounded font-black text-[10px] uppercase shadow-lg transition-transform active:scale-95"
                                    >
                                        + Add Floating Text
                                    </button>
                                </div>

                                <div className="p-4 border-b border-gray-700 bg-gray-950/20">
                                    <h3 className="text-[10px] font-bold text-gray-500 uppercase mb-3">Play Library</h3>
                                    <div className="mb-4 bg-gray-900/50 rounded-lg border border-gray-700 p-2 max-h-48 overflow-y-auto qr-custom-scrollbar">
                                        <div
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${!activeFolderId ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-white/5'}`}
                                            onClick={() => setActiveFolderId(null)}
                                        >
                                            <span>📂</span> All Plays
                                        </div>
                                        {folderTree.map(f => (
                                            <SimplifiedFolderRow
                                                key={f.id}
                                                folder={f}
                                                level={0}
                                                activeFolderId={activeFolderId}
                                                setActiveFolderId={setActiveFolderId}
                                                expandedFolders={expandedFolders}
                                                setExpandedFolders={setExpandedFolders}
                                            />
                                        ))}
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Search plays..."
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>

                                <div className="flex-1 overflow-y-auto p-2 space-y-1 qr-custom-scrollbar">
                                    {filteredPlays.map(p => (
                                        <div
                                            key={p.id}
                                            draggable
                                            onDragStart={(e) => e.dataTransfer.setData('play', JSON.stringify(p))}
                                            className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg cursor-grab hover:bg-gray-700 transition-colors group"
                                        >
                                            <div className="text-sm font-bold text-white truncate">{p.playName || 'Untitled'}</div>
                                            <div className="text-[10px] text-gray-500 uppercase tracking-wider">{p.formation || 'MISC'}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 bg-gray-950 p-8 overflow-y-auto flex flex-col items-center qr-custom-scrollbar">
                        <div className="mb-6 flex items-center gap-6 bg-gray-800/50 p-3 rounded-xl border border-gray-700 w-full max-w-4xl">
                            <div className="flex-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Global UI Scale (Screen only)</label>
                                <input
                                    type="range" min="0.3" max="1.5" step="0.01"
                                    className="w-full accent-blue-500"
                                    value={globalScale}
                                    onChange={(e) => setGlobalScale(parseFloat(e.target.value))}
                                />
                            </div>
                            <button
                                onClick={handlePrint}
                                className="bg-green-600 hover:bg-green-500 text-white px-8 py-3 rounded-xl font-black text-lg shadow-xl shadow-green-900/20 transition-all active:scale-95 flex items-center gap-3"
                            >
                                <span className="text-2xl">⎙</span> PRINT NOW
                            </button>
                        </div>

                        {/* PREVIEW CONTAINER */}
                        <div
                            className={`qr-print-preview-container bg-white shadow-2xl flex flex-col items-center ${mode === 'single' ? 'qr-landscape' : 'qr-portrait'}`}
                            style={{
                                height: mode === 'single' ? '8.5in' : '11in',
                                width: mode === 'single' ? '11in' : '8.5in',
                                minHeight: mode === 'single' ? '8.5in' : '11in',
                                overflow: 'visible',
                                backgroundColor: '#fff',
                                boxSizing: 'border-box'
                            }}
                        >
                            {/* Screen-only scale wrapper */}
                            <div style={{
                                transform: `scale(${globalScale})`,
                                transformOrigin: 'top center',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                itemsCenter: 'center'
                            }} className="qr-screen-scaler">
                                {mode === 'single' ? (
                                    <div className="flex flex-col gap-20 items-center">
                                        {singlePages.map((page, pageIdx) => (
                                            <div
                                                key={page.id}
                                                className={`relative cursor-pointer ${printStyle === 'app' ? `theme-${appTheme}` : 'bg-white'}`}
                                                style={{
                                                    width: '11in',
                                                    height: '8.5in',
                                                    background: printStyle === 'app' ? 'var(--bg-app)' : '#fff',
                                                    padding: '0.5in',
                                                    boxSizing: 'border-box'
                                                }}
                                                onClick={() => setSelectedPageIdx(pageIdx)}
                                                onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    setSlotContextMenu({
                                                        x: e.clientX,
                                                        y: e.clientY,
                                                        cardIdx: pageIdx,
                                                        slotIdx: 0,
                                                        mode: 'single'
                                                    });
                                                }}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={(e) => {
                                                    const raw = e.dataTransfer.getData('play');
                                                    if (!raw) return;
                                                    try {
                                                        const p = JSON.parse(raw);
                                                        handleAssignPlay(pageIdx, 0, p);
                                                    } catch (err) {
                                                        console.error("Failed to parse play data", err);
                                                    }
                                                }}
                                            >
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRemoveSinglePage(pageIdx); }}
                                                    className="absolute -right-12 top-0 w-8 h-8 bg-red-600 text-white rounded-full no-print hover:bg-red-500 flex items-center justify-center font-bold shadow-lg"
                                                > × </button>

                                                <div className={`w-[10in] h-[7.5in] flex flex-col items-center justify-center p-12 border shadow-sm relative overflow-hidden ${printStyle === 'app' ? 'bg-[var(--bg-panel)] border-white/5' : 'bg-white border-gray-100'}`}>
                                                    <h3
                                                        className={`text-center mb-8 border-b-8 inline-block uppercase tracking-tighter font-black ${printStyle === 'app' ? 'text-[var(--text-primary)] border-[var(--accent-color)]' : 'text-black border-black'}`}
                                                        style={{
                                                            fontSize: (page.titleSize || 32) + 'px',
                                                            background: (page.reads && page.reads.length > 0)
                                                                ? (page.reads.length === 1 ? page.reads[0] : (page.reads.length === 2
                                                                    ? `linear-gradient(to right, ${page.reads[0]} 0% 50%, ${page.reads[1]} 50% 100%)`
                                                                    : `linear-gradient(to right, ${page.reads[0]} 0% 33.3%, ${page.reads[1]} 33.3% 66.6%, ${page.reads[2]} 66.6% 100%)`))
                                                                : 'transparent',
                                                            padding: (page.reads && page.reads.length > 0) ? '12px 32px' : '0 32px 12px 32px',
                                                            color: (page.reads && page.reads.length > 0) ? (['#ffffff', '#fbbf24'].includes(page.reads[0]) ? 'black' : 'white') : undefined
                                                        }}
                                                    >
                                                        {page.title || page.play?.playName || 'Untitled Play'}
                                                    </h3>
                                                    <div className="flex-1 w-full relative flex items-center justify-center overflow-hidden">
                                                        <div
                                                            className="w-full h-full max-h-full"
                                                            onMouseDown={(e) => onSlotMouseDown(e, pageIdx, 0)}
                                                            onWheel={(e) => onSlotWheel(e, pageIdx, 0)}
                                                        >
                                                            <div style={{ transform: `scale(${page.scale}) translate(${page.offsetX}px, ${page.offsetY}px)`, width: '100%', height: '100%' }}>
                                                                <Field
                                                                    isPrintMode={printStyle === 'ink-saver'}
                                                                    players={(page.play?.players || []).filter(p => !printSettings.hideBall || p.type !== 'ball')}
                                                                    routes={page.hideRoutes ? [] : (page.play?.routes || [])}
                                                                    selectedTool="select"
                                                                    viewMode="landscape"
                                                                    iconSize={1.2}
                                                                    playbackState={{ isPlaying: false, progress: 0 }}
                                                                    ballSpec={(page.play?.ballSpec && !printSettings.hideBall) ? page.play.ballSpec : { enabled: false }}
                                                                    globalSettings={{ ...globalSettings, hidePlayerLabels: printSettings.hideLabels }}
                                                                    ballWizardStep={0} wristbandGuide={{ enabled: false }}
                                                                    isPrinting={true}
                                                                    showGridlines={!printSettings.hideFieldLines}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Pages Draggable Annotations */}
                                                    {(page.annotations || []).map(ann => (
                                                        <div
                                                            key={ann.id}
                                                            className="absolute cursor-move select-none no-print-border group hover:border border-transparent hover:border-blue-400 px-1 rounded transition-colors"
                                                            style={{
                                                                left: ann.x + '%',
                                                                top: ann.y + '%',
                                                                fontSize: ann.fontSize + 'px',
                                                                fontWeight: ann.fontWeight,
                                                                color: ann.color,
                                                                zIndex: 100
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                setIsDragging(true);
                                                                dragStartLoc.current = {
                                                                    x: e.clientX, y: e.clientY,
                                                                    startX: ann.x, startY: ann.y,
                                                                    type: 'annotation',
                                                                    annId: ann.id,
                                                                    parentType: 'single',
                                                                    parentIdx: pageIdx
                                                                };
                                                            }}
                                                        >
                                                            <input
                                                                className="bg-transparent border-none outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                                                                value={ann.text}
                                                                onChange={(e) => handleUpdateAnnotation(ann.id, 'single', pageIdx, { text: e.target.value })}
                                                                onClick={e => e.stopPropagation()}
                                                                style={{ width: (ann.text.length + 1) + 'ch' }}
                                                            />
                                                            <button
                                                                className="opacity-0 group-hover:opacity-100 absolute -top-4 -right-4 w-6 h-6 bg-red-500 text-white text-xs rounded-full no-print flex items-center justify-center shadow-lg transition-opacity"
                                                                onClick={() => handleDeleteAnnotation(ann.id, 'single', pageIdx)}
                                                            >×</button>
                                                        </div>
                                                    ))}


                                                    <div className={`mt-6 pt-6 text-[11px] uppercase font-black tracking-[0.2em] border-t w-full text-center ${printStyle === 'app' ? 'text-[var(--text-secondary)] border-white/10' : 'text-black border-gray-100'}`}>
                                                        QuickRoute Play Designer &bull; {new Date().toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div
                                        className="qr-wristband-container relative w-full"
                                        style={{ height: '11in', width: '8.5in', backgroundColor: '#fff', position: 'relative' }}
                                        onMouseMove={handleCardDragMove}
                                        onMouseUp={handleCardDragEnd}
                                        onMouseLeave={handleCardDragEnd}
                                        onClick={handleContainerClick}
                                    >
                                        {/* Simplified container without extraneous grids */}
                                        {/* WRISTBAND CARDS REGION */}
                                        {wristbandCards.map((card, cardIdx) => (
                                            <div
                                                key={card.id}
                                                className={`wristband-card-print absolute bg-white select-none shadow-sm ${draggingCardIdx === cardIdx ? 'ring-2 ring-yellow-400 cursor-move' : 'cursor-default'}`}
                                                style={{
                                                    left: `${card.posX.toFixed(4)}in`,
                                                    top: `${card.posY.toFixed(4)}in`,
                                                    width: `${card.width.toFixed(4)}in`,
                                                    height: `${card.height.toFixed(4)}in`,
                                                    outline: draggingCardIdx === cardIdx ? '3px solid #facc15' : (resizingCardIdx === cardIdx ? '3px solid #10b981' : '1px dashed #000'),
                                                    outlineOffset: '-1px',
                                                    boxSizing: 'border-box',
                                                    overflow: 'visible', // CRITICAL: Allow debug bar to hang outside
                                                    backgroundColor: '#fff',
                                                    flexShrink: 0,
                                                    zIndex: (draggingCardIdx === cardIdx || resizingCardIdx === cardIdx) ? 100 : 1
                                                }}
                                                onDoubleClick={(e) => onCardDoubleClick(e, cardIdx)}
                                                onClick={(e) => {
                                                    if (draggingCardIdx === cardIdx) {
                                                        e.stopPropagation();
                                                        setDraggingCardIdx(null);
                                                    }
                                                }}
                                            >
                                                {/* 1. CLIPPED CONTENT LAYER (Title & Plays) */}
                                                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                                                    {/* Title bar */}
                                                    <div
                                                        className="w-full flex items-center justify-center border-b border-black text-black font-black uppercase tracking-widest bg-gray-50 pointer-events-auto"
                                                        style={{ height: `${card.titleHeight || 38}px`, fontSize: `${card.titleSize || 12}px` }}
                                                    >
                                                        {card.title || 'Wristband Card'}
                                                    </div>

                                                    {/* Internal Play Grid */}
                                                    <div className="flex pointer-events-auto" style={{
                                                        width: '100%',
                                                        height: `calc(100% - ${card.titleHeight || 38}px)`,
                                                        paddingLeft: `${card.marginLR || 0}in`,
                                                        paddingRight: `${card.marginLR || 0}in`
                                                    }}>
                                                        <div className="grid grid-cols-3 grid-rows-2 w-full h-full">
                                                            {card.slots.map((slot, slotIdx) => (
                                                                <div
                                                                    key={slotIdx}
                                                                    onContextMenu={(e) => {
                                                                        e.preventDefault();
                                                                        setSlotContextMenu({
                                                                            x: e.clientX,
                                                                            y: e.clientY,
                                                                            cardIdx,
                                                                            slotIdx
                                                                        });
                                                                    }}
                                                                    onWheel={(e) => onSlotWheel(e, cardIdx, slotIdx)}
                                                                    onMouseDown={(e) => {
                                                                        if (e.button === 0) onSlotMouseDown(e, cardIdx, slotIdx);
                                                                    }}
                                                                    className={`relative border-b border-r border-black flex flex-col items-center justify-center transition-all group cursor-move select-none ${!slot.play ? 'bg-gray-50' : 'bg-white'} ${selectedSlot?.cardIdx === cardIdx && selectedSlot?.slotIdx === slotIdx ? 'ring-2 ring-blue-500 ring-inset z-10' : ''}`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        boxSizing: 'border-box',
                                                                        borderRight: (slotIdx + 1) % 3 === 0 ? 'none' : '1px solid black',
                                                                        borderBottom: slotIdx >= 3 ? 'none' : '1px solid black'
                                                                    }}
                                                                    onDragOver={(e) => e.preventDefault()}
                                                                    onDrop={(e) => {
                                                                        const raw = e.dataTransfer.getData('play');
                                                                        if (!raw) return;
                                                                        try {
                                                                            const p = JSON.parse(raw);
                                                                            handleAssignPlay(cardIdx, slotIdx, p);
                                                                        } catch (err) {
                                                                            console.error("Drop error:", err);
                                                                        }
                                                                    }}
                                                                >
                                                                    {!slot.play ? (
                                                                        <div className="flex flex-col items-center opacity-30">
                                                                            <div className="text-[8px] font-bold">PLAY {slotIdx + 1}</div>
                                                                            <div className="text-[6px]">DROP HERE</div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="w-full h-full relative overflow-hidden flex flex-col box-border border-b border-black last:border-b-0">
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                inset: 0,
                                                                                overflow: 'hidden',
                                                                                transform: `scale(${slot.scale}) translate(${slot.offsetX}px, ${slot.offsetY}px)`,
                                                                                transformOrigin: 'top center'
                                                                            }}>
                                                                                <Field
                                                                                    players={(slot.play.players || []).filter(p => !printSettings.hideBall || p.type !== 'ball')}
                                                                                    routes={slot.play.routes || []}
                                                                                    ballSpec={slot.play.ballSpec && !printSettings.hideBall ? slot.play.ballSpec : { enabled: false }}
                                                                                    isPrintMode={printStyle === 'ink-saver'}
                                                                                    isPrinting={true}
                                                                                    globalSettings={{ ...globalSettings, hidePlayerLabels: printSettings.hideLabels }}
                                                                                    wristbandGuide={{ enabled: false }}
                                                                                    selectedTool="select"
                                                                                    viewMode="landscape"
                                                                                    isLocked={true}
                                                                                    showGridlines={!printSettings.hideFieldLines}
                                                                                />
                                                                            </div>

                                                                            {/* Slot Header - Overlay */}
                                                                            <div
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    right: 0,
                                                                                    background: (slot.reads && slot.reads.length > 0)
                                                                                        ? (slot.reads.length === 1 ? slot.reads[0] : (slot.reads.length === 2
                                                                                            ? `linear-gradient(to right, ${slot.reads[0]} 0% 50%, ${slot.reads[1]} 50% 100%)`
                                                                                            : `linear-gradient(to right, ${slot.reads[0]} 0% 33.3%, ${slot.reads[1]} 33.3% 66.6%, ${slot.reads[2]} 66.6% 100%)`))
                                                                                        : (slot.headerColor || card.headerColor || '#000000'),
                                                                                    backgroundColor: (slot.headerColor || card.headerColor || '#000000'),
                                                                                    color: slot.headerTextColor || (['#ffffff', '#fbbf24'].includes(slot.headerColor || card.headerColor) ? 'black' : 'white'),
                                                                                    fontSize: `${slot.headerTextSize || (card.headerHeight || 12) - 4}px`,
                                                                                    fontWeight: 'bold',
                                                                                    textAlign: 'center',
                                                                                    padding: '2px 0',
                                                                                    height: `${card.headerHeight || 12}px`,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    width: '100%',
                                                                                    zIndex: 10
                                                                                }}
                                                                            >
                                                                                {slot.alias || slot.play.playName}
                                                                            </div>

                                                                            {/* Slot Sub-Header (Optional Second Line) */}
                                                                            {slot.subAlias && (
                                                                                <div
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        top: `${card.headerHeight || 12}px`,
                                                                                        left: 0,
                                                                                        right: 0,
                                                                                        color: slot.subAliasColor || '#ffffff',
                                                                                        fontSize: `${(card.headerHeight || 12) - 4}px`,
                                                                                        fontWeight: '500',
                                                                                        textAlign: 'center',
                                                                                        padding: '0px 0',
                                                                                        height: `${card.headerHeight || 12}px`,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        width: '100%',
                                                                                        zIndex: 9,
                                                                                        textShadow: '0 0 2px black'
                                                                                    }}
                                                                                >
                                                                                    {slot.subAlias}
                                                                                </div>
                                                                            )}
                                                                            {/* Slot Metadata (No-Print) */}
                                                                            <div className="absolute bottom-0 left-0 right-0 bg-transparent text-black text-[5px] px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex justify-end uppercase font-bold tracking-tighter no-print">
                                                                                <span>{Math.round(slot.scale * 100)}%</span>
                                                                            </div>
                                                                            <button
                                                                                className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 no-print"
                                                                                onClick={(e) => { e.stopPropagation(); handleClearSlot(cardIdx, slotIdx); }}
                                                                            >×</button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 2. CARD TOOLS (Annotations, Resize, Remove) - NOT CLIPPED */}
                                                {(card.annotations || []).map(ann => (
                                                    ann.type === 'stamp' ? (
                                                        <div
                                                            key={ann.id}
                                                            className="absolute cursor-move select-none z-50 hover:ring-2 ring-blue-500"
                                                            style={{
                                                                left: ann.x + '%',
                                                                top: ann.y + '%',
                                                                width: '12px',
                                                                height: '12px',
                                                                backgroundColor: ann.color,
                                                                border: '1px solid white'
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                setIsDragging(true);
                                                                dragStartLoc.current = { x: e.clientX, y: e.clientY, startX: ann.x, startY: ann.y, type: 'annotation', annId: ann.id, parentType: 'wristband', parentIdx: cardIdx };
                                                            }}
                                                        >
                                                            <button
                                                                className="absolute -top-3 -right-3 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] opacity-0 hover:opacity-100 no-print"
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteAnnotation(ann.id, 'wristband', cardIdx); }}
                                                            >×</button>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            key={ann.id}
                                                            className="absolute cursor-move select-none z-50 px-1 border border-transparent hover:border-blue-400 group flex items-center gap-1"
                                                            style={{ left: ann.x + '%', top: ann.y + '%', fontSize: ann.fontSize + 'px', color: ann.color, fontWeight: ann.fontWeight }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                setIsDragging(true);
                                                                dragStartLoc.current = { x: e.clientX, y: e.clientY, startX: ann.x, startY: ann.y, type: 'annotation', annId: ann.id, parentType: 'wristband', parentIdx: cardIdx };
                                                            }}
                                                        >
                                                            <input
                                                                className="bg-transparent border-none outline-none text-current font-inherit"
                                                                value={ann.text}
                                                                onChange={(e) => handleUpdateAnnotation(ann.id, 'wristband', cardIdx, { text: e.target.value })}
                                                                style={{ width: (ann.text.length + 1) + 'ch', fontWeight: 'inherit' }}
                                                            />
                                                            {/* Text Controls */}
                                                            <div className="absolute -top-6 left-0 bg-gray-800 rounded flex items-center opacity-0 group-hover:opacity-100 transition-opacity no-print">
                                                                <button
                                                                    className={`w-5 h-5 text-[10px] text-white ${ann.fontWeight === 'bold' ? 'font-bold bg-blue-600' : ''}`}
                                                                    onClick={(e) => { e.stopPropagation(); handleUpdateAnnotation(ann.id, 'wristband', cardIdx, { fontWeight: ann.fontWeight === 'bold' ? 'normal' : 'bold' }); }}
                                                                >B</button>
                                                            </div>
                                                        </div>
                                                    )
                                                ))}

                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRemoveWristbandCard(cardIdx); }}
                                                    className="no-drag absolute -right-12 top-0 w-8 h-8 bg-black text-white rounded-full no-print hover:bg-red-500 flex items-center justify-center font-bold"
                                                >×</button>

                                                {/* DEBUG BAR - THE FAMOUS BLUE BAR */}
                                                <div className="absolute -bottom-10 left-0 bg-[#002244] border-2 border-blue-400 text-white text-[11px] px-4 py-1.5 rounded-full shadow-[0_10px_30px_rgba(0,0,0,0.5)] z-[200] no-print whitespace-nowrap flex gap-4 items-center">
                                                    <div className="font-mono font-bold text-cyan-400">
                                                        USER SCALE: {card.width.toFixed(2)}"w x {card.height.toFixed(2)}"h
                                                    </div>
                                                    {draggingCardIdx === cardIdx && (
                                                        <div className="bg-yellow-400 text-black px-2 py-0.5 rounded font-black text-[9px] uppercase animate-pulse">
                                                            PICKED UP - CLICK BACKGROUND TO DROP
                                                        </div>
                                                    )}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleCloneWristbandCard(cardIdx); }}
                                                        className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded-full font-bold text-[9px] uppercase transition-all active:scale-95 flex items-center gap-1"
                                                    >
                                                        <span>📂</span> Clone
                                                    </button>
                                                </div>

                                                {/* RESIZE HANDLE */}
                                                <div
                                                    className="no-drag absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize bg-blue-600 hover:bg-blue-400 z-[150] no-print flex items-center justify-center text-white shadow-xl rounded-tl-lg"
                                                    onMouseDown={(e) => handleCardResizeStart(e, cardIdx)}
                                                >
                                                    <div className="w-3 h-3 border-r-4 border-b-4 border-white/50"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <p className="mt-8 text-gray-500 text-[10px] text-center max-w-lg mb-20 no-print">
                            {mode === 'wristband'
                                ? "WYSIWYG: 8.5\" x 11\" Letter Paper. Each card is 4.25\" x 2.33\"."
                                : "WYSIWYG: 11\" x 8.5\" Landscape Paper. Standard coaching binder size."}
                        </p>
                    </div>

                    <div className="fixed bottom-0 left-0 right-0 bg-yellow-100 p-2 text-center text-xs text-yellow-800 font-bold border-t border-yellow-200 no-print z-[2000]">
                        Print Settings: Scale 100%. If cards get cut off, your printer margins are too large for side-by-side 10.5cm cards.
                    </div>

                    {/* SLOT CONTEXT MENU */}
                    {slotContextMenu && (
                        <div
                            className="fixed z-[3000] bg-gray-800 border border-gray-600 rounded-lg shadow-2xl py-2 min-w-[200px] animate-in fade-in zoom-in duration-100"
                            style={{ left: slotContextMenu.x, top: slotContextMenu.y }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="px-3 py-1.5 border-b border-gray-700 mb-1">
                                <div className="text-[10px] font-bold text-gray-500 uppercase">{slotContextMenu.mode === 'single' ? 'Single Play' : `Slot ${slotContextMenu.slotIdx + 1}`} Options</div>
                            </div>

                            <button
                                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                onClick={() => {
                                    const activeMode = slotContextMenu.mode || mode;
                                    let currentVal = "";
                                    if (activeMode === 'single') {
                                        currentVal = singlePages[slotContextMenu.cardIdx].title || singlePages[slotContextMenu.cardIdx].play?.playName || "";
                                    } else {
                                        const slot = wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx];
                                        currentVal = slot.alias || slot.play?.playName || "";
                                    }
                                    const newAlias = prompt("Enter name (blank to reset):", currentVal);
                                    if (newAlias !== null) {
                                        handleUpdateAlias(slotContextMenu.cardIdx, slotContextMenu.slotIdx, newAlias, activeMode);
                                    }
                                    setSlotContextMenu(null);
                                }}
                            >
                                <span>✏️</span> {slotContextMenu.mode === 'single' ? 'Rename Page' : 'Rename (Alias)'}
                            </button>

                            <button
                                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                onClick={() => {
                                    setReadsPicker({
                                        x: slotContextMenu.x + 20,
                                        y: slotContextMenu.y + 20,
                                        cardIdx: slotContextMenu.cardIdx,
                                        slotIdx: slotContextMenu.slotIdx,
                                        mode: slotContextMenu.mode || 'wristband'
                                    });
                                    setSlotContextMenu(null);
                                }}
                            >
                                <span>🎯</span> {slotContextMenu.mode === 'single' ? 'Set Page Reads' : 'Set Slot Reads'}
                            </button>

                            <button
                                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                onClick={() => {
                                    const page = singlePages[slotContextMenu.cardIdx];
                                    const activeMode = slotContextMenu.mode || mode;
                                    const currentPlay = (activeMode === 'single') ? page.play : wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx].play;
                                    if (!currentPlay) return;
                                    if (activeMode === 'single') {
                                        handleUpdateSinglePage(slotContextMenu.cardIdx, { play: flipPlay(currentPlay) });
                                    } else {
                                        handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { play: flipPlay(currentPlay) });
                                    }
                                    setSlotContextMenu(null);
                                }}
                            >
                                <span>↔️</span> Flip Play
                            </button>

                            {slotContextMenu.mode === 'single' && (
                                <button
                                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                    onClick={() => {
                                        const page = singlePages[slotContextMenu.cardIdx];
                                        handleUpdateSinglePage(slotContextMenu.cardIdx, { hideRoutes: !page.hideRoutes });
                                        setSlotContextMenu(null);
                                    }}
                                >
                                    <span>{singlePages[slotContextMenu.cardIdx].hideRoutes ? '👁️' : '🚫'}</span> {singlePages[slotContextMenu.cardIdx].hideRoutes ? 'Show Routes' : 'Hide Routes'}
                                </button>
                            )}

                            {slotContextMenu.mode !== 'single' && (
                                <>
                                    <button
                                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                        onClick={() => {
                                            const slot = wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx];
                                            const newSubAlias = prompt("Enter second line text (blank to hide):", slot.subAlias || "");
                                            if (newSubAlias !== null) {
                                                handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { subAlias: newSubAlias });
                                            }
                                            setSlotContextMenu(null);
                                        }}
                                    >
                                        <span>📝</span> Edit Second Line
                                    </button>

                                    <div className="flex gap-1 px-4 py-2 hover:bg-gray-700 transition-colors items-center group border-b border-gray-700">
                                        <span className="text-xs text-gray-400 mr-2">L2 Color:</span>
                                        {['#000000', '#ffffff', '#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#db2777', '#6b7280'].map(color => (
                                            <button key={color} className={`w-4 h-4 rounded-full border border-gray-500 ${wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx].subAliasColor === color ? 'ring-2 ring-blue-500' : ''}`} style={{ backgroundColor: color }} onClick={() => handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { subAliasColor: color })} />
                                        ))}
                                    </div>

                                    <div className="flex gap-1 px-4 py-2 hover:bg-gray-700 transition-colors items-center group border-b border-gray-700">
                                        <span className="text-xs text-gray-400 mr-2">Hdr Clr:</span>
                                        {['#000000', '#1f2937', '#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#6b7280', '#ffffff'].map(color => (
                                            <button key={color} className={`w-4 h-4 rounded-full border border-gray-500 ${wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx].headerColor === color ? 'ring-2 ring-blue-500' : ''}`} style={{ backgroundColor: color }} onClick={() => handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { headerColor: color })} />
                                        ))}
                                    </div>

                                    <div className="flex gap-1 px-4 py-2 hover:bg-gray-700 transition-colors items-center group border-b border-gray-700">
                                        <span className="text-xs text-gray-400 mr-2">Txt Clr:</span>
                                        {['#000000', '#ffffff', '#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#db2777', '#6b7280'].map(color => (
                                            <button key={color} className={`w-4 h-4 rounded-full border border-gray-500 ${wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx].headerTextColor === color ? 'ring-2 ring-blue-500' : ''}`} style={{ backgroundColor: color }} onClick={() => handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { headerTextColor: color })} />
                                        ))}
                                    </div>
                                </>
                            )}

                            <button
                                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                onClick={() => {
                                    const activeMode = slotContextMenu.mode || mode;
                                    if (activeMode === 'single') {
                                        handleUpdateSinglePage(slotContextMenu.cardIdx, { scale: (singlePages[slotContextMenu.cardIdx].scale || 1.0) + 0.1 });
                                    } else {
                                        const s = wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx];
                                        handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { scale: (s.scale || 1.0) + 0.1 });
                                    }
                                }}
                            >
                                <span>➕</span> Zoom In
                            </button>

                            <button
                                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                                onClick={() => {
                                    const activeMode = slotContextMenu.mode || mode;
                                    if (activeMode === 'single') {
                                        handleUpdateSinglePage(slotContextMenu.cardIdx, { scale: Math.max(0.1, (singlePages[slotContextMenu.cardIdx].scale || 1.0) - 0.1) });
                                    } else {
                                        const s = wristbandCards[slotContextMenu.cardIdx].slots[slotContextMenu.slotIdx];
                                        handleUpdateSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx, { scale: Math.max(0.1, (s.scale || 1.0) - 0.1) });
                                    }
                                }}
                            >
                                <span>➖</span> Zoom Out
                            </button>

                            {slotContextMenu.mode !== 'single' && (
                                <>
                                    <div className="px-3 py-1.5 mt-2 border-t border-gray-700">
                                        <div className="text-[10px] font-bold text-gray-500 uppercase">Swap With</div>
                                    </div>
                                    {[0, 1, 2, 3, 4, 5].map(idx => (
                                        idx !== slotContextMenu.slotIdx && (
                                            <button
                                                key={idx}
                                                className="w-full text-left px-4 py-1.5 text-xs text-gray-300 hover:bg-gray-700 transition-colors"
                                                onClick={() => {
                                                    handleSwapSlots(slotContextMenu.cardIdx, slotContextMenu.slotIdx, idx);
                                                    setSlotContextMenu(null);
                                                }}
                                            >
                                                Slot {idx + 1} {wristbandCards[slotContextMenu.cardIdx].slots[idx].play ? `(${wristbandCards[slotContextMenu.cardIdx].slots[idx].alias || wristbandCards[slotContextMenu.cardIdx].slots[idx].play.playName})` : '(Empty)'}
                                            </button>
                                        )
                                    ))}

                                    <div className="h-[1px] bg-gray-700 my-1" />
                                    <button
                                        className="w-full text-left px-4 py-2 text-sm text-red-100 hover:bg-red-900/30 transition-colors flex items-center gap-2"
                                        onClick={() => {
                                            handleClearSlot(slotContextMenu.cardIdx, slotContextMenu.slotIdx);
                                            setSlotContextMenu(null);
                                        }}
                                    >
                                        <span>🗑️</span> Clear Slot
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {readsPicker && (
                        <div
                            className="fixed z-[2000] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-4 w-72"
                            style={{ left: readsPicker.x, top: readsPicker.y }}
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-white font-black text-sm uppercase tracking-widest flex items-center gap-2">
                                    <span>🎯</span> Reads
                                </h4>
                                <button onClick={() => setReadsPicker(null)} className="text-gray-400 hover:text-white">✕</button>
                            </div>

                            {(() => {
                                const play = readsPicker.mode === 'wristband'
                                    ? wristbandCards[readsPicker.cardIdx].slots[readsPicker.slotIdx].play
                                    : singlePages[readsPicker.cardIdx].play;

                                if (!play) return <div className="text-[10px] text-gray-500">No play assigned</div>;

                                const currentReads = (readsPicker.mode === 'wristband'
                                    ? wristbandCards[readsPicker.cardIdx].slots[readsPicker.slotIdx].reads
                                    : singlePages[readsPicker.cardIdx].reads) || [];

                                const toggleRead = (player) => {
                                    let nextReads = [...currentReads];
                                    const color = player.color || (player.type === 'offense' ? (globalSettings?.offenseColor || '#fbbf24') : '#ef4444');

                                    if (nextReads.includes(color)) {
                                        nextReads = nextReads.filter(c => c !== color);
                                    } else if (nextReads.length < 3) {
                                        nextReads.push(color);
                                    }

                                    if (readsPicker.mode === 'wristband') {
                                        handleUpdateSlot(readsPicker.cardIdx, readsPicker.slotIdx, { reads: nextReads });
                                    } else {
                                        const newPages = [...singlePages];
                                        newPages[readsPicker.cardIdx].reads = nextReads;
                                        setSinglePages(newPages);
                                    }
                                };

                                return (
                                    <div className="space-y-4">
                                        <div className="flex gap-2">
                                            {[0, 1, 2].map(i => (
                                                <div key={i} className="flex-1 h-8 rounded border border-gray-700 flex items-center justify-center bg-black/20 overflow-hidden"
                                                    style={{ background: currentReads[i] || 'transparent' }}>
                                                    <span className="text-[10px] text-white mix-blend-difference font-black">{i + 1}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                                            {(play.players || []).filter(p => p.type === 'offense').map(p => {
                                                const effectiveColor = p.color || (p.type === 'offense' ? (globalSettings?.offenseColor || '#fbbf24') : '#ef4444');
                                                const isSelected = currentReads.includes(effectiveColor);
                                                return (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => toggleRead(p)}
                                                        className={`flex items-center gap-2 p-2 rounded border transition-all text-left ${isSelected ? 'bg-blue-600/20 border-blue-500' : 'bg-gray-800 border-gray-700 hover:bg-gray-700'}`}
                                                    >
                                                        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: effectiveColor }} />
                                                        <span className="text-[10px] text-white truncate">{p.label || p.role}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => {
                                                if (readsPicker.mode === 'wristband') {
                                                    handleUpdateSlot(readsPicker.cardIdx, readsPicker.slotIdx, { reads: [] });
                                                } else {
                                                    const newPages = [...singlePages];
                                                    newPages[readsPicker.cardIdx].reads = [];
                                                    setSinglePages(newPages);
                                                }
                                            }}
                                            className="w-full py-2 text-[10px] uppercase font-black text-gray-400 hover:text-white border border-gray-700 rounded transition-colors"
                                        >
                                            Clear Reads
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    <style jsx>{`
            @media print {
                html, body {
                    background-color: white !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }

                body * { visibility: hidden; }
                .qr-print-preview-container, .qr-print-preview-container * { visibility: visible !important; }
                
                .qr-print-preview-container { 
                    position: absolute !important; 
                    left: 0 !important;
                    top: 0 !important;
                    margin: 0 !important; 
                    width: 8.5in !important;
                    min-height: 11in !important;
                    padding: 0 !important;
                    box-shadow: none !important;
                    background: white !important;
                    display: block !important;
                }

                .qr-screen-scaler {
                    transform: none !important;
                    display: block !important;
                    height: auto !important;
                }

                @page {
                    size: ${mode === 'wristband' ? 'letter portrait' : 'letter landscape'};
                    margin: 0;
                }
                
                .qr-landscape {
                    width: 11in !important;
                    height: 8.5in !important;
                }
                .qr-portrait {
                    width: 8.5in !important;
                    height: 11in !important;
                }

                .qr-wristband-container {
                    /* 
                       TRICK: Set container to 0 size with visible overflow. 
                       This prevents the browser from thinking the "page content" is 8.5" wide 
                       and scaling it down to fit 8.0" printable area.
                       Absolute positioned cards will just print where they are.
                    */
                    padding: 0 !important;
                    margin: 0 !important;
                    gap: 0 !important;
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 0px !important;
                    height: 0px !important;
                    overflow: visible !important;
                    display: block !important;
                }

                .no-print { display: none !important; }

                /* Force exact physical units on print */
                .wristband-card-print {
                    position: absolute !important;
                    /* Use border-box with thick outline to ensure size is visibly correct without affecting box model */
                    border: 1px dashed black !important;
                    outline: none !important;
                    margin: 0 !important;
                    transform: none !important;
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                }
            }

            .wristband-card-print {
                box-sizing: border-box;
                page-break-inside: avoid;
                flex-shrink: 0;
                transition: border 0.2s;
            }
            .wristband-card-print:hover {
                border-color: #3b82f6 !important;
            }
        `}</style>
                </div>
            </div >
        </div >
    );
}
